<template>
	<div>
		<div class="flex flex-col gap-3 md:flex-row md:flex-wrap md:items-start md:justify-between md:gap-4">
			<div class="flex items-start min-w-0 gap-3 md:gap-4">
				<div
					class="h-8 w-8 shrink-0 overflow-hidden rounded-full border border-gray-200 bg-center bg-cover md:h-[72px] md:w-[72px]"
					:style="applicantAvatarStyle"
				/>
				<div class="min-w-0">
					<div class="flex flex-wrap items-center gap-3">
						<h2
							class="m-0 text-2xl font-extrabold tracking-wide uppercase text-neutral-900 md:text-3xl"
						>
							{{ applicantFullName }}
						</h2>
						<ApplicantProfileDeactivatedBadge v-if="isOrgContext" :application="application" />
						<TopFitBadge
							v-if="showTopFitBadge"
							:variant="topFitBadgeVariant"
						/>
					</div>

					<div
						class="mt-1 flex flex-nowrap items-center gap-4 overflow-x-auto text-sm text-neutral-500 md:flex-wrap md:overflow-visible"
					>
						<!-- Email -->
						<span
							v-if="applicantEmail"
							class="inline-flex shrink-0 items-center gap-2 whitespace-nowrap md:shrink md:whitespace-normal"
						>
							<a
								:href="emailHref"
								class="inline-flex items-center gap-2 hover:text-neutral-900"
								:aria-label="
									$t('general.applicant-email-aria-label', {
										email: applicantEmail
									})
								"
							>
								<IconEnvelope size="14" />
								{{ applicantEmail }}
							</a>
						</span>

						<!-- Phone -->
						<span
							v-if="applicantPhone"
							class="inline-flex shrink-0 items-center gap-2 whitespace-nowrap md:shrink md:whitespace-normal"
						>
							<a
								:href="phoneHref"
								class="inline-flex items-center gap-2 hover:text-neutral-900"
								:aria-label="
									$t('general.applicant-phone-aria-label', {
										phone: applicantPhone
									})
								"
							>
								<IconPhone size="14" />
								{{ applicantPhone }}
							</a>
						</span>

						<!-- LinkedIn profile link -->
						<a
							v-if="applicantProfileLink"
							:href="applicantProfileLink"
							target="_blank"
							rel="noopener noreferrer"
							class="inline-flex shrink-0 items-center gap-2 whitespace-nowrap text-sm text-neutral-500 hover:text-neutral-900 md:shrink md:whitespace-normal"
						>
							<IconExternalLink size="14" />
							{{ $t('general.view-linkedin-profile') }}
						</a>
					</div>
				</div>
			</div>

			<ApplicationActionButtons
				v-if="showActionButtons"
				class="max-w-full md:w-auto"
				:application="application"
				:job-offer="jobOffer"
				:is-org="isOrgContext"
				:loading="applicationLoading"
				:candidate-downloads="candidateDownloads"
				@open-actions="$emit('open-actions')"
				@download="$emit('download', $event)"
				:show-invite="!application"
				variant="full"
				@invite="handleInvite"
				@update="handleUpdate"
				@contact="handleContact"
			/>
		</div>
	</div>
</template>

<script>
import ApplicantProfileDeactivatedBadge from '~/components/Applications/ApplicantProfileDeactivatedBadge.vue'
import { TopFitBadge } from '~/components/Applications'
import ApplicationActionButtons from './ApplicationActionButtons.vue'
import {
	IconPhone,
	IconEnvelope,
	IconExternalLink
} from '~/components/Icons'

export default {
	name: 'ApplicantProfileHeader',
	components: {
		ApplicantProfileDeactivatedBadge,
		ApplicationActionButtons,
		TopFitBadge,
		IconPhone,
		IconEnvelope,
		IconExternalLink
	},
	props: {
		candidateDownloads: { type: Object, default: () => ({}) },
		applicantProfileId: {
			type: [String, Number],
			required: true
		},
		jobOfferId: {
			type: [String, Number],
			default: null
		},
		applicantAvatarStyle: {
			type: Object,
			required: true
		},
		applicantFullName: {
			type: String,
			required: true
		},
		applicantEmail: {
			type: String,
			default: null
		},
		applicantPhone: {
			type: String,
			default: null
		},
		applicantProfileLink: {
			type: String,
			default: null
		},
		showTopFitBadge: {
			type: Boolean,
			default: false
		},
		showActionButtons: {
			type: Boolean,
			default: false
		},
		application: {
			type: Object,
			default: null
		},
		jobOffer: {
			type: Object,
			default: null
		},
		isOrgContext: {
			type: Boolean,
			default: false
		},
		applicationLoading: {
			type: [Boolean, Array],
			default: false
		}
	},
	computed: {
		topFitBadgeVariant() {
			return this.application?.status === 'denied' ? 'declined' : 'default'
		},
		emailHref() {
			if (!this.applicantEmail) {
				return null
			}

			return `mailto:${this.applicantEmail.trim()}`
		},
		phoneHref() {
			if (!this.applicantPhone) {
				return null
			}

			// Keep only characters supported by tel URI.
			const normalizedPhone = this.applicantPhone
				.trim()
				.replace(/[^+\d().\-\s]/g, '')
				.replace(/\s+/g, '')

			return `tel:${normalizedPhone}`
		}
	},
	methods: {
		handleInvite() {
			this.$emit('invite', this.applicantProfileId, this.jobOfferId)
		},
		handleUpdate(applicationId, status, sendRejectionEmail, rejectionResult) {
			this.$emit(
				'update',
				applicationId,
				status,
				sendRejectionEmail,
				...(rejectionResult ? [rejectionResult] : [])
			)
		},
		handleContact() {
			if (this.application?.id) {
				this.$emit('contact', this.application.id)
			}
		}
	}
}
</script>
